/**
 * Track Entity Details sections (ADR-041 D-5; REQ-UI-174 / -177) — the seven
 * sections rendered when the ``inspected`` slot holds ``{kind: "track"}``:
 * IDENTITY · SUMMARY · HEAD · GEOMETRY · DETECTIONS · ALSO REPORTED BY ·
 * PROVENANCE. Section ids are the BFF's ``_TRACK_SECTION_IDS`` roster
 * (mirrored in :data:`use-section-visibility.SECTION_IDS.track`).
 *
 * # Data
 *
 * The summary comes from ``GET /api/query/object-track/{track_id}``
 * (:func:`useObjectTrackDetail` — summary + related tracks) and is
 * overlaid by the live store's record when the track is buffered, so a
 * track that is still moving reads live. The DETECTIONS section lists the
 * live store's members (every read role); for a track that is not in the
 * live buffer it reaches for the analyst-gated range route
 * (:func:`useTrackDetections`) and, for an operator, says why the members
 * are out of reach rather than showing nothing.
 *
 * # Cross-pane links
 *
 * Links *navigate* Entity Details (``inspect`` — the ``inspected`` slot only,
 * the map ring stays put); the producer link also surfaces the Platforms
 * list (``inspectInList``). Pushing the track to the map is the Tracks-panel
 * row's "Select on Map" (REQ-UI-172), the single push-to-map affordance.
 *
 * Cross-references:
 *   - REQ-UI-174 (the sections); REQ-UI-177 (also reported by — a relation,
 *     never fusion; ADR-041 D-1); REQ-UI-064 (section gear); REQ-UI-084
 *     (coordinate format); REQ-UI-110 (producer display name).
 *   - entity-details/detection-sections.tsx (the sibling this mirrors).
 */
import { useState } from "react";
import { SectionShell, SectionRow, SectionPlaceholder } from "@/entity-details/section-shell";
import { useSectionVisibility } from "@/entity-details/use-section-visibility";
import { EntityDetailsSectionsProvider } from "@/entity-details/entity-details-sections-context";
import {
  NO_WIRE_BYTES_NOTE,
  recordTypeForEntityKind,
} from "@/entity-details/entity-record-mapping";
import { SourceBytesSection } from "@/query/source-bytes-section";
import { useObjectTrackDetail } from "@/entity-details/use-object-track-detail";
import {
  TRACK_DETECTIONS_PAGE_SIZE,
  useTrackDetections,
} from "@/entity-details/use-track-detections";
import { useSelectionActions } from "@/selection/selection-store";
import { useInspectInList } from "@/selection/use-inspect-in-list";
import { classificationLabel, formatConfidence } from "@/detections/detection-display";
import { usePreferences } from "@/preferences/preferences-context";
import { useProducerNames } from "@/lib/use-producer-names";
import { shortId } from "@/lib/short-id";
import { formatCoordinate } from "@/units/format-coordinate";
import {
  type ObjectTrackRecord,
  type TrackSummary,
  positionedMembers,
  useObjectTrackById,
} from "@/object-tracks/object-track-store";
import {
  formatDuration,
  formatMemberCount,
  formatTrackClock,
  summaryEnvelope,
  trackPathLengthM,
} from "@/object-tracks/track-display";
import type { components } from "@/lib/api-types.gen";

type RelatedTrackItem = components["schemas"]["RelatedTrackItem"];

export function TrackSections({ trackId }: { trackId: string }): React.ReactElement {
  const detail = useObjectTrackDetail({ trackId });
  const live = useObjectTrackById(trackId);
  // A link in this panel is a traversal: it pushes the selection being left
  // onto the navigation history, so the back control returns the operator to
  // the track they came from (REQ-UI-186) — including a track reached from the
  // Query panel, where re-finding it would otherwise mean re-running the query.
  const { inspectLinked } = useSelectionActions();
  const inspectInList = useInspectInList();
  const names = useProducerNames();
  const sections = useSectionVisibility("track");
  const { isVisible } = sections;

  // The freshest summary wins: the live store's (moving) over the fetched.
  const summary: TrackSummary | null = live?.summary ?? detail.data?.summary ?? null;
  const related: readonly RelatedTrackItem[] = detail.data?.related ?? [];
  const loading = detail.isLoading && summary === null;
  const error = detail.isError ? detail.error.message : null;
  const stateNote = loading
    ? "Loading track…"
    : error !== null
      ? `Could not load track: ${error}`
      : summary === null
        ? "Track not found — it may have aged out of the track summary's retention."
        : null;

  return (
    <EntityDetailsSectionsProvider value={{ entityType: "track", sections }}>
      {isVisible("identity") && (
        <SectionShell id="identity" title="Identity">
          <SectionRow
            label="Track ID"
            value={
              <code className="text-body-sm text-text-primary" title={trackId}>
                {shortId(trackId, "TRK")}
              </code>
            }
          />
          {stateNote !== null && <SectionPlaceholder note={stateNote} />}
          {summary !== null && (
            <>
              <SectionRow
                label="Object ID"
                value={
                  <code className="text-body-sm text-text-primary" title={summary.object_id}>
                    {shortId(summary.object_id, "OBJ")}
                  </code>
                }
              />
              <SectionRow
                label="Platform"
                value={
                  <button
                    type="button"
                    className="font-mono text-body-sm text-meridian-action hover:underline"
                    title={summary.producer_id}
                    data-testid="track-section-producer-link"
                    onClick={() => inspectInList({ kind: "platform", id: summary.producer_id })}
                  >
                    {names.nameFor(summary.producer_id)}
                  </button>
                }
              />
            </>
          )}
        </SectionShell>
      )}

      {isVisible("summary") && (
        <SectionShell id="summary" title="Summary">
          {summary === null && <SectionPlaceholder note={stateNote ?? "—"} />}
          {summary !== null && (
            <>
              <SectionRow label="First seen" value={formatTimestamp(summary.first_time)} />
              <SectionRow label="Last seen" value={formatTimestamp(summary.last_time)} />
              <SectionRow label="Duration" value={formatDuration(summary.duration_seconds)} />
              <SectionRow
                label="Detections"
                value={
                  <span data-testid="track-section-count">
                    {formatMemberCount(summary.detection_count)}
                  </span>
                }
              />
              <SectionRow
                label="Positioned"
                value={`${summary.positioned_count} of ${summary.detection_count}`}
              />
              <SectionRow label="Class" value={classificationLabel(summary.primary_class)} />
              <SectionRow label="Head state" value={summary.head_state ?? "—"} />
              <SectionRow
                label="Head confidence"
                value={formatConfidence(summary.head_confidence)}
              />
            </>
          )}
        </SectionShell>
      )}

      {isVisible("head") && (
        <SectionShell id="head" title="Head">
          {summary === null && <SectionPlaceholder note={stateNote ?? "—"} />}
          {summary !== null && (
            <>
              <SectionRow
                label="Detection"
                value={
                  <button
                    type="button"
                    className="font-mono text-body-sm text-meridian-action hover:underline"
                    title={summary.head_report_id}
                    data-testid="track-section-head-link"
                    onClick={() => inspectLinked({ kind: "detection", id: summary.head_report_id })}
                  >
                    {shortId(summary.head_report_id, "DET")} →
                  </button>
                }
              />
              <SectionRow label="Time" value={formatTimestamp(summary.last_time)} />
              <HeadPositionRow summary={summary} />
            </>
          )}
        </SectionShell>
      )}

      {isVisible("geometry") && <GeometrySection summary={summary} live={live} note={stateNote} />}

      {isVisible("detections") && (
        <DetectionsSection summary={summary} live={live} note={stateNote} />
      )}

      {isVisible("also_reported_by") && (
        <SectionShell id="also_reported_by" title="Also reported by">
          {summary === null && <SectionPlaceholder note={stateNote ?? "—"} />}
          {summary !== null && related.length === 0 && (
            <SectionPlaceholder note="No other sensor has reported this object." />
          )}
          {related.map((item) => (
            <SectionRow
              key={item.track_id}
              label={names.nameFor(item.producer_id)}
              value={
                <button
                  type="button"
                  className="font-mono text-body-sm text-meridian-action hover:underline"
                  title={item.track_id}
                  data-testid={`track-section-related-${item.track_id}`}
                  onClick={() => inspectLinked({ kind: "track", id: item.track_id })}
                >
                  {formatMemberCount(item.detection_count)} · last seen{" "}
                  {formatTrackClock(item.last_time)} →
                </button>
              }
            />
          ))}
        </SectionShell>
      )}

      {isVisible("provenance") && (
        <SectionShell id="provenance" title="Provenance">
          {summary === null && <SectionPlaceholder note={stateNote ?? "—"} />}
          {summary !== null && (
            <>
              <SectionRow
                label="Source network"
                value={
                  summary.source_network !== null ? (
                    <span data-testid="track-section-crossed-cds">
                      {summary.source_network}{" "}
                      <span className="rounded border border-border px-1 text-caption uppercase text-text-muted">
                        crossed CDS
                      </span>
                    </span>
                  ) : (
                    "Local"
                  )
                }
              />
              <SectionRow
                label="Aggregator"
                value={summary.source_aggregator_id !== "" ? summary.source_aggregator_id : "—"}
              />
              <SectionRow label="Schema version" value={String(summary.schema_version)} />
              <SectionRow label="Summary updated" value={formatTimestamp(summary.updated_at)} />
            </>
          )}
        </SectionShell>
      )}
      {/* A track carries no wire bytes — it is a derivation over detections,
          with no payload column for the BFF to reconstruct from. The section
          renders anyway and states that, because on a pane whose sibling
          kinds all have bytes an absent section reads as a fault
          (REQ-UI-184; CLAUDE.md §8.1). */}
      {isVisible("source_bytes") && (
        <SourceBytesSection
          typeId={recordTypeForEntityKind("track")}
          recordId={trackId}
          noBytesNote={NO_WIRE_BYTES_NOTE.track}
        />
      )}
    </EntityDetailsSectionsProvider>
  );
}

function HeadPositionRow({ summary }: { summary: TrackSummary }): React.ReactElement {
  const format = usePreferences().workingState.units?.coordinate_format ?? "decimal_degrees";
  if (summary.head_latitude_deg === null || summary.head_longitude_deg === null) {
    return (
      <SectionRow
        label="Position"
        value="Approximate — the head carries no resolved location (range/bearing)"
      />
    );
  }
  return (
    <SectionRow
      label="Position"
      value={formatCoordinate(summary.head_latitude_deg, summary.head_longitude_deg, format)}
    />
  );
}

function formatLengthM(metres: number): string {
  return metres >= 1000 ? `${(metres / 1000).toFixed(2)} km` : `${Math.round(metres)} m`;
}

function GeometrySection({
  summary,
  live,
  note,
}: {
  summary: TrackSummary | null;
  live: ObjectTrackRecord | null;
  note: string | null;
}): React.ReactElement {
  const format = usePreferences().workingState.units?.coordinate_format ?? "decimal_degrees";
  const positioned = live !== null ? positionedMembers(live) : [];
  const env = summary !== null ? summaryEnvelope(summary) : null;
  return (
    <SectionShell id="geometry" title="Geometry">
      {summary === null && <SectionPlaceholder note={note ?? "—"} />}
      {summary !== null && env === null && (
        <SectionPlaceholder note="No member carries a resolved location — the track has no extent and draws no line (REQ-UI-071)." />
      )}
      {summary !== null && env !== null && (
        <>
          <SectionRow label="Extent SW" value={formatCoordinate(env.minLat, env.minLon, format)} />
          <SectionRow label="Extent NE" value={formatCoordinate(env.maxLat, env.maxLon, format)} />
          <SectionRow
            label="Path length"
            value={
              positioned.length >= 2 ? (
                <span data-testid="track-section-path-length">
                  {formatLengthM(trackPathLengthM(positioned))}
                </span>
              ) : (
                "— (members not buffered)"
              )
            }
          />
          {summary.positioned_count < summary.detection_count && (
            <SectionRow
              label="Approximate"
              value={`${summary.detection_count - summary.positioned_count} member(s) carry no resolved location and draw no vertex`}
            />
          )}
        </>
      )}
    </SectionShell>
  );
}

/**
 * DETECTIONS — the track's member detections, one fixed-size page at a
 * time (REQ-UI-174 amended 2026-10-05).
 *
 * # Why it pages
 *
 * A track can hold 100k+ detections. The live path used to render every
 * buffered member at once (up to ``TRACK_MAX_MEMBERS`` = 500) and the cold
 * path *appended* each "Load more" result to the last, so the section grew
 * without bound in the DOM and reaching page four meant walking pages two
 * and three into it. One pager now serves both paths, and the Tracks
 * panel's row expansion — capped at ten, a quick look — points here for
 * the full ordered set (REQ-UI-187).
 *
 * # Why "Previous" is a held cursor, not a request
 *
 * The range route pages by **forward-only keyset cursor** (REQ-QUERY-052):
 * there is no backwards cursor to ask for and no count to jump by. So the
 * section keeps the cursors it has visited, one per page index, and
 * stepping back re-reads a window the operator already disclosed — which
 * TanStack serves from cache, so it emits no second audit event for a read
 * already made. Direct page-number entry is deliberately absent: it would
 * need a cursor the server cannot produce.
 *
 * # Three paths, one control
 *
 * Live members (every read role, from the shared store), the analyst-gated
 * cold range fetch, and — for an operator looking at an aged-out track —
 * the note saying why the members are out of reach rather than a blank
 * section or a 403.
 */
function DetectionsSection({
  summary,
  live,
  note,
}: {
  summary: TrackSummary | null;
  live: ObjectTrackRecord | null;
  note: string | null;
}): React.ReactElement {
  // Member links are traversals too (REQ-UI-186) — see the note in
  // ``TrackSections`` above.
  const { inspectLinked } = useSelectionActions();
  // Newest first, like every timeline in the product.
  const liveMembers = live !== null && live.members.length > 0 ? [...live.members].reverse() : null;

  // One page index drives both paths. For the cold path it also indexes
  // ``cursors``: entry *i* is the cursor that fetches page *i*, so entry 0 is
  // always ``null`` (the first page needs none) and a forward step appends the
  // cursor the server just handed back.
  const [pageIndex, setPageIndex] = useState(0);
  const [cursors, setCursors] = useState<readonly (string | null)[]>([null]);

  const cold = useTrackDetections({
    track:
      liveMembers === null && summary !== null
        ? {
            producer_id: summary.producer_id,
            object_id: summary.object_id,
            first_time: summary.first_time,
            last_time: summary.last_time,
          }
        : null,
    cursor: cursors[pageIndex] ?? null,
  });

  const start = pageIndex * TRACK_DETECTIONS_PAGE_SIZE;
  const liveRows = liveMembers?.slice(start, start + TRACK_DETECTIONS_PAGE_SIZE) ?? null;
  const coldRows = cold.data?.items ?? null;

  const hasPrev = pageIndex > 0;
  const hasNext =
    liveMembers !== null
      ? start + TRACK_DETECTIONS_PAGE_SIZE < liveMembers.length
      : cold.data?.next_cursor != null;

  const goNext = (): void => {
    if (liveMembers === null) {
      const next = cold.data?.next_cursor;
      if (next == null) return;
      // Record the cursor for the page we are stepping onto, so stepping back
      // and forward again re-uses it rather than re-deriving it.
      setCursors((held) => (held.length > pageIndex + 1 ? held : [...held, next]));
    }
    setPageIndex((index) => index + 1);
  };

  return (
    <SectionShell id="detections" title="Detections">
      {summary === null && <SectionPlaceholder note={note ?? "—"} />}
      {summary !== null && liveRows !== null && (
        <>
          <MemberTable
            rows={liveRows.map((m) => ({
              report_id: m.report_id,
              time: m.time,
              primary_class: m.primary_class,
              detection_confidence: m.detection_confidence,
              located: m.latitude_deg !== null && m.longitude_deg !== null,
            }))}
            onSelect={(id) => inspectLinked({ kind: "detection", id })}
          />
          <DetectionsPager
            pageIndex={pageIndex}
            shown={liveRows.length}
            total={liveMembers?.length ?? 0}
            hasPrev={hasPrev}
            hasNext={hasNext}
            onPrev={() => setPageIndex((index) => Math.max(0, index - 1))}
            onNext={goNext}
          />
          {/* The live buffer is ring-capped, so a long track's older members
              are simply not held. Said, not implied by a short list: the
              summary's own count is the honest denominator (CLAUDE.md §8.1). */}
          {liveMembers !== null && summary.detection_count > liveMembers.length && (
            <p
              className="mt-1 text-caption text-text-muted"
              data-testid="track-section-detections-partial"
            >
              The live buffer holds the most recent {liveMembers.length.toLocaleString("en-US")} of
              this track&apos;s {summary.detection_count.toLocaleString("en-US")} detections. The
              rest are queryable by analysts.
            </p>
          )}
        </>
      )}
      {summary !== null && liveMembers === null && !cold.canQuery && (
        <SectionPlaceholder note="This track is outside the live window. Its detections are queryable by analysts (Query panel → Detections, filtered by this object id)." />
      )}
      {summary !== null && liveMembers === null && cold.canQuery && (
        <>
          {cold.isLoading && <SectionPlaceholder note="Loading detections…" />}
          {cold.isError && (
            <SectionPlaceholder note={`Could not load detections: ${cold.error.message}`} />
          )}
          {coldRows !== null && coldRows.length > 0 && (
            <>
              <MemberTable
                rows={coldRows.map((d) => ({
                  report_id: d.report_id,
                  time: d.time,
                  primary_class: d.primary_class,
                  detection_confidence: d.detection_confidence,
                  located: d.latitude_deg !== null && d.longitude_deg !== null,
                }))}
                onSelect={(id) => inspectLinked({ kind: "detection", id })}
              />
              <DetectionsPager
                pageIndex={pageIndex}
                shown={coldRows.length}
                total={summary.detection_count}
                hasPrev={hasPrev}
                hasNext={hasNext}
                onPrev={() => setPageIndex((index) => Math.max(0, index - 1))}
                onNext={goNext}
              />
            </>
          )}
        </>
      )}
    </SectionShell>
  );
}

/**
 * The pager: where this page sits in the whole set, and the two steps.
 *
 * Renders **nothing** when the first page is the whole set — a pager whose
 * buttons can never do anything is chrome the operator learns to ignore
 * (CLAUDE.md §8.1). Previous/Next only: the cold path's keyset cursors are
 * forward-only, so a page-number box would need a cursor the server cannot
 * produce, and offering one that silently did nothing would be worse than
 * not offering it.
 */
function DetectionsPager({
  pageIndex,
  shown,
  total,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
}: {
  pageIndex: number;
  shown: number;
  total: number;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}): React.ReactElement | null {
  if (!hasPrev && !hasNext) return null;
  const first = pageIndex * TRACK_DETECTIONS_PAGE_SIZE + 1;
  const last = pageIndex * TRACK_DETECTIONS_PAGE_SIZE + shown;
  return (
    <nav
      aria-label="Detection pages"
      className="mt-2 flex items-center gap-2 text-body-sm text-text-secondary"
    >
      <button
        type="button"
        onClick={onPrev}
        disabled={!hasPrev}
        data-testid="track-section-detections-prev"
        className="rounded border border-border px-2 py-0.5 text-text-primary hover:bg-bg-elevated disabled:opacity-40"
      >
        Previous
      </button>
      <span data-testid="track-section-detections-position">
        Detections {first.toLocaleString("en-US")}–{last.toLocaleString("en-US")} of{" "}
        {total.toLocaleString("en-US")}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={!hasNext}
        data-testid="track-section-detections-next"
        className="rounded border border-border px-2 py-0.5 text-text-primary hover:bg-bg-elevated disabled:opacity-40"
      >
        Next
      </button>
    </nav>
  );
}

interface MemberTableRow {
  report_id: string;
  time: string;
  primary_class: string | null;
  detection_confidence: number | null;
  located: boolean;
}

function MemberTable({
  rows,
  onSelect,
}: {
  rows: readonly MemberTableRow[];
  onSelect: (reportId: string) => void;
}): React.ReactElement {
  return (
    <table className="w-full text-body-sm" data-testid="track-section-detections-table">
      <caption className="sr-only">Detections of this track, newest first</caption>
      <thead>
        <tr className="text-left text-caption uppercase tracking-wide text-text-muted">
          <th scope="col" className="py-1 pr-2 font-medium">
            Time
          </th>
          <th scope="col" className="py-1 pr-2 font-medium">
            Class
          </th>
          <th scope="col" className="py-1 pr-2 text-right font-medium">
            Conf.
          </th>
          <th scope="col" className="py-1 font-medium">
            <span className="sr-only">Detection</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.report_id} data-testid={`track-section-detection-${row.report_id}`}>
            <td className="py-0.5 pr-2 font-mono text-text-secondary">
              {formatTrackClock(row.time)}
              {!row.located && (
                <span
                  className="ml-1 text-caption uppercase text-text-muted"
                  aria-label="approximate position"
                >
                  approx
                </span>
              )}
            </td>
            <td className="py-0.5 pr-2 text-text-primary">
              {classificationLabel(row.primary_class)}
            </td>
            <td className="py-0.5 pr-2 text-right font-mono text-text-secondary">
              {formatConfidence(row.detection_confidence)}
            </td>
            <td className="py-0.5">
              <button
                type="button"
                className="font-mono text-meridian-action hover:underline"
                title={row.report_id}
                onClick={() => onSelect(row.report_id)}
              >
                {shortId(row.report_id, "DET")} →
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function formatTimestamp(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
